import {
  evaluateParam,
  findClip,
  formatTimecode,
  sequenceDuration,
  setParam,
  type MediaAsset,
  type ParamKey,
} from '@ve/core';
import { PreviewEngine } from '@ve/engine';
import {
  AudioLines,
  ChevronFirst,
  ChevronLast,
  Pause,
  Play,
  Plus,
  Repeat,
  StepBack,
  StepForward,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { gesture, useEditor } from '../../state/editor-store.ts';
import { mediaService } from '../../media/setup.ts';
import { useMediaRuntime } from '../../media/service.ts';
import { transport, usePlayback } from '../../state/playback.ts';
import { errorMessage, toast } from '../../state/toast-store.ts';
import { commandTip, tip } from '../../lib/tooltip.ts';
import { insertMedia } from '../../lib/commands.ts';
import {
  attachSourceEngine,
  closeSourcePreview,
  sourceProject,
  useSourcePreview,
} from '../../state/source-preview.ts';

type PreviewScale = 1 | 0.5 | 0.25;

/** Tells an engine when a media item's sources change (came online, proxy generated, relinked). */
function followMediaChanges(engine: PreviewEngine): () => void {
  let prev = useMediaRuntime.getState().runtime;
  return useMediaRuntime.subscribe((s) => {
    if (s.runtime === prev) return;
    for (const [id, rt] of Object.entries(s.runtime)) {
      if (prev[id]?.sources !== rt.sources || prev[id]?.availability !== rt.availability)
        engine.invalidateMedia(id);
    }
    prev = s.runtime;
  });
}

/** Fits a `w`×`h` stage into the element, preserving the aspect ratio. */
function useFitSize(ref: React.RefObject<HTMLElement | null>, w: number, h: number) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      // Content box: the area's padding is not available to the stage.
      const style = getComputedStyle(el);
      const aw = el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const ah = el.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const scale = Math.max(0, Math.min(aw / w, ah / h));
      setSize({ width: Math.floor(w * scale), height: Math.floor(h * scale) });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, w, h]);
  return size;
}

export function Viewer() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<PreviewEngine | null>(null);
  const [previewScale, setPreviewScale] = useState<PreviewScale>(0.5);
  const [useProxies, setUseProxies] = useState(true);

  const project = useEditor((s) => s.project)!;
  const seqId = useEditor((s) => s.activeSequenceId) ?? project.rootSequenceId;
  const loopRange = useEditor((s) => s.loopRange);
  const loopEnabled = useEditor((s) => s.loopEnabled);
  const { width: pw, height: ph } = project.settings;

  // Engine lifecycle.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let engine: PreviewEngine;
    try {
      engine = new PreviewEngine({ canvas, resolveMedia: mediaService.resolve });
    } catch (err) {
      toast.error(`Preview unavailable: ${errorMessage(err)}`);
      return;
    }
    engineRef.current = engine;
    const detach = usePlayback.getState().attach(engine);
    const unsubMedia = followMediaChanges(engine);
    return () => {
      unsubMedia();
      detach();
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    engineRef.current?.setProject(project, seqId);
  }, [project, seqId]);

  useEffect(() => {
    engineRef.current?.seek(0);
  }, [seqId]);

  useEffect(() => {
    engineRef.current?.setLoop(loopEnabled && loopRange ? loopRange : null);
  }, [loopEnabled, loopRange]);

  useEffect(() => {
    engineRef.current?.setPreviewResolution(previewScale);
  }, [previewScale]);

  useEffect(() => {
    engineRef.current?.setUseProxies(useProxies);
  }, [useProxies]);

  // Fit the canvas into the available area, preserving the project aspect ratio.
  const size = useFitSize(areaRef, pw, ph);

  // A library item clicked for preview replaces the timeline in the viewer until closed.
  const sourceId = useSourcePreview((s) => s.mediaId);
  const sourceAsset = sourceId !== null ? project.media[sourceId] : undefined;
  useEffect(() => {
    if (sourceId !== null && !sourceAsset) closeSourcePreview(); // removed from the project
  }, [sourceId, sourceAsset]);
  useEffect(() => {
    if (sourceAsset) engineRef.current?.pause();
  }, [sourceAsset]);

  return (
    <section className={`panel viewer${sourceAsset ? ' source-mode' : ''}`}>
      {sourceAsset && <SourceHeader name={sourceAsset.name} mediaId={sourceAsset.id} />}
      <div
        className="viewer-area"
        ref={areaRef}
        hidden={!!sourceAsset}
        onPointerDown={(e) => e.target === e.currentTarget && useEditor.getState().clearSelection()}
      >
        <div className="viewer-stage" style={{ width: size.width, height: size.height }}>
          <canvas ref={canvasRef} className="viewer-canvas" />
          <TransformOverlay scale={size.width / pw} />
        </div>
      </div>
      {sourceAsset && <SourceStage asset={sourceAsset} previewScale={previewScale} useProxies={useProxies} />}
      <Transport
        previewScale={previewScale}
        setPreviewScale={setPreviewScale}
        useProxies={useProxies}
        setUseProxies={setUseProxies}
        durationFrames={sequenceDuration(project, seqId)}
        source={!!sourceAsset}
      />
    </section>
  );
}

function SourceHeader({ name, mediaId }: { name: string; mediaId: string }) {
  return (
    <div className="source-header">
      <span className="source-badge">Source</span>
      <span className="source-name">{name}</span>
      <span className="spacer" />
      <button
        className="btn small ghost"
        onClick={() => insertMedia(mediaId)}
        {...tip('Add this to the timeline at the playhead')}
      >
        <Plus size={13} /> Add to timeline
      </button>
      <button
        className="btn small ghost icon"
        onClick={closeSourcePreview}
        {...tip('Back to the timeline', 'Escape')}
      >
        <X size={14} />
      </button>
    </div>
  );
}

/** Plays one library item on its own engine, so the timeline playhead is untouched. */
function SourceStage({
  asset,
  previewScale,
  useProxies,
}: {
  asset: MediaAsset;
  previewScale: PreviewScale;
  useProxies: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<PreviewEngine | null>(null);
  const settings = useEditor((s) => s.project!.settings);
  const project = useMemo(() => sourceProject(asset, settings), [asset, settings]);
  const size = useFitSize(areaRef, project.settings.width, project.settings.height);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let engine: PreviewEngine;
    try {
      engine = new PreviewEngine({ canvas, resolveMedia: mediaService.resolve });
    } catch (err) {
      toast.error(`Preview unavailable: ${errorMessage(err)}`);
      return;
    }
    engineRef.current = engine;
    const detach = attachSourceEngine(engine);
    const unsubMedia = followMediaChanges(engine);
    return () => {
      unsubMedia();
      detach();
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  // A different item starts from its beginning, and plays right away (unless it's a still).
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setProject(project, project.rootSequenceId);
    useSourcePreview.setState({ duration: engine.duration });
  }, [project]);
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.pause();
    engine.seek(0);
    if (asset.kind !== 'image') engine.play();
  }, [asset.id, asset.kind]);

  useEffect(() => engineRef.current?.setPreviewResolution(previewScale), [previewScale]);
  useEffect(() => engineRef.current?.setUseProxies(useProxies), [useProxies]);

  return (
    <div className="viewer-area" ref={areaRef}>
      <div className="viewer-stage" style={{ width: size.width, height: size.height }}>
        <canvas ref={canvasRef} className="viewer-canvas" />
        {asset.kind === 'audio' && (
          <div className="source-audio">
            <AudioLines size={40} />
            <span>{asset.name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Transport({
  previewScale,
  setPreviewScale,
  useProxies,
  setUseProxies,
  durationFrames: timelineDuration,
  source,
}: {
  previewScale: PreviewScale;
  setPreviewScale(s: PreviewScale): void;
  useProxies: boolean;
  setUseProxies(v: boolean): void;
  durationFrames: number;
  /** Controls the library source preview instead of the timeline. */
  source: boolean;
}) {
  const timelineFrame = usePlayback((s) => s.frame);
  const timelinePlaying = usePlayback((s) => s.playing);
  const sourceState = useSourcePreview();
  const frame = source ? sourceState.frame : timelineFrame;
  const playing = source ? sourceState.playing : timelinePlaying;
  const durationFrames = source ? sourceState.duration : timelineDuration;
  const fps = useEditor((s) => s.project!.settings.fps);
  const loopEnabled = useEditor((s) => s.loopEnabled);
  const setLoopEnabled = useEditor((s) => s.setLoopEnabled);
  return (
    <div className="transport">
      <span
        className="timecode big"
        {...tip(
          source ? 'Position in the previewed clip' : 'Playhead position (hours:minutes:seconds:frames)',
        )}
      >
        {formatTimecode(frame, fps)}
      </span>
      <div className="transport-buttons">
        <button
          className="btn ghost icon"
          {...commandTip('home', 'Go to start')}
          onClick={() => transport.seekViewer(0)}
        >
          <ChevronFirst size={16} />
        </button>
        <button
          className="btn ghost icon"
          {...commandTip('stepBack', 'Previous frame (hold Shift for 10)')}
          onClick={() => transport.step(-1)}
        >
          <StepBack size={16} />
        </button>
        <button
          className="btn icon play-button"
          {...commandTip('play', playing ? 'Pause' : 'Play')}
          onClick={transport.toggle}
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button
          className="btn ghost icon"
          {...commandTip('stepFwd', 'Next frame (hold Shift for 10)')}
          onClick={() => transport.step(1)}
        >
          <StepForward size={16} />
        </button>
        <button
          className="btn ghost icon"
          {...commandTip('end', 'Go to end')}
          onClick={() => transport.seekViewer(durationFrames)}
        >
          <ChevronLast size={16} />
        </button>
        {!source && (
          <button
            className={`btn ghost icon${loopEnabled ? ' active' : ''}`}
            {...commandTip(
              'loopToggle',
              loopEnabled ? 'Stop looping' : 'Loop playback between in (I) and out (O)',
            )}
            onClick={() => setLoopEnabled(!loopEnabled)}
          >
            <Repeat size={14} />
          </button>
        )}
      </div>
      <div className="transport-right">
        <label
          className="muted checkbox-label"
          {...tip('Play low-resolution proxy copies for smoother preview. Export always uses the originals.')}
        >
          <input type="checkbox" checked={useProxies} onChange={(e) => setUseProxies(e.target.checked)} />{' '}
          Proxies
        </label>
        <select
          className="select"
          value={previewScale}
          {...tip('Preview render resolution — lower is faster, export is unaffected')}
          onChange={(e) => setPreviewScale(Number(e.target.value) as PreviewScale)}
        >
          <option value={1}>Full</option>
          <option value={0.5}>1/2</option>
          <option value={0.25}>1/4</option>
        </select>
        <span className="timecode muted" {...tip(source ? 'Clip duration' : 'Timeline duration')}>
          {formatTimecode(durationFrames, fps)}
        </span>
      </div>
    </div>
  );
}

/**
 * On-canvas handles for the selected visual clip: drag to move, corners to scale (Shift: non-uniform),
 * top handle to rotate. Writes keyframes when the parameter is animated.
 */
function TransformOverlay({ scale }: { scale: number }) {
  const project = useEditor((s) => s.project)!;
  const selected = useEditor((s) => (s.selection.clipIds.length === 1 ? s.selection.clipIds[0] : undefined));
  const frame = usePlayback((s) => s.frame);
  const engine = usePlayback((s) => s.engine);
  // Geometry is produced by the engine's render; re-read it after every render.
  const [, setRenderTick] = useState(0);
  useEffect(() => engine?.onRender(() => setRenderTick((t) => (t + 1) % 1e9)), [engine]);
  const loc = selected ? findClip(project, selected) : null;
  const clip = loc?.clip;
  const active = clip && clip.type !== 'audio' && frame >= clip.start && frame < clip.start + clip.duration;
  const geometry = active && engine ? engine.getLayerGeometry(clip.id) : null;
  if (!clip || !geometry) return null;

  const { width: pw, height: ph } = project.settings;
  const toScreen = (p: { x: number; y: number }) => ({ x: p.x * scale, y: p.y * scale });
  const corners = geometry.corners.map(toScreen);
  const center = {
    x: corners.reduce((a, c) => a + c.x, 0) / 4,
    y: corners.reduce((a, c) => a + c.y, 0) / 4,
  };
  const rel = frame - clip.start;
  const read = (k: ParamKey) => evaluateParam(clip, k, rel);

  const drag = (
    e: React.PointerEvent,
    label: string,
    apply: (dx: number, dy: number, ev: PointerEvent) => Partial<Record<ParamKey, number>>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    gesture.begin(label);
    const move = (ev: PointerEvent) => {
      const values = apply((ev.clientX - sx) / scale, (ev.clientY - sy) / scale, ev);
      gesture.replace(label, (p) => {
        for (const [k, v] of Object.entries(values)) setParam(p, clip.id, k as ParamKey, v, rel);
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      gesture.commit();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const x0 = read('x');
  const y0 = read('y');
  const s0 = read('scale');
  const sx0 = read('scaleX');
  const sy0 = read('scaleY');
  const r0 = read('rotation');
  // Centre of the layer in project pixels, for scale/rotate math.
  const cx = pw / 2 + x0;
  const cy = ph / 2 + y0;

  const onCorner = (e: React.PointerEvent) => {
    const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
    const px = (e.clientX - rect.left) / scale;
    const py = (e.clientY - rect.top) / scale;
    const d0 = Math.hypot(px - cx, py - cy) || 1;
    const ax0 = Math.abs(px - cx) || 1;
    const ay0 = Math.abs(py - cy) || 1;
    drag(e, 'Scale', (dx, dy, ev) => {
      if (ev.shiftKey) {
        return { scaleX: sx0 * (Math.abs(px + dx - cx) / ax0), scaleY: sy0 * (Math.abs(py + dy - cy) / ay0) };
      }
      return { scale: s0 * (Math.hypot(px + dx - cx, py + dy - cy) / d0) };
    });
  };

  const onRotate = (e: React.PointerEvent) => {
    const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
    const px = (e.clientX - rect.left) / scale;
    const py = (e.clientY - rect.top) / scale;
    const a0 = Math.atan2(py - cy, px - cx);
    drag(e, 'Rotate', (dx, dy, ev) => {
      let deg = r0 + ((Math.atan2(py + dy - cy, px + dx - cx) - a0) * 180) / Math.PI;
      if (ev.shiftKey) deg = Math.round(deg / 15) * 15;
      return { rotation: deg };
    });
  };

  const top = { x: (corners[0]!.x + corners[1]!.x) / 2, y: (corners[0]!.y + corners[1]!.y) / 2 };
  const ux = top.x - center.x;
  const uy = top.y - center.y;
  const len = Math.hypot(ux, uy) || 1;
  const rotHandle = { x: top.x + (ux / len) * 24, y: top.y + (uy / len) * 24 };

  return (
    <svg className="transform-overlay">
      <polygon
        points={corners.map((c) => `${c.x},${c.y}`).join(' ')}
        className="transform-box"
        {...tip('Drag to move')}
        onPointerDown={(e) =>
          drag(e, 'Move', (dx, dy) => ({ x: Math.round(x0 + dx), y: Math.round(y0 + dy) }))
        }
      />
      <line x1={top.x} y1={top.y} x2={rotHandle.x} y2={rotHandle.y} className="transform-line" />
      <circle
        cx={rotHandle.x}
        cy={rotHandle.y}
        r={5}
        className="transform-rotate"
        onPointerDown={onRotate}
        {...tip('Drag to rotate (Shift snaps to 15°)')}
      />
      {corners.map((c, i) => (
        <rect
          key={i}
          x={c.x - 5}
          y={c.y - 5}
          width={10}
          height={10}
          className="transform-handle"
          {...tip('Drag to scale (Shift: width/height independently)')}
          onPointerDown={onCorner}
        />
      ))}
    </svg>
  );
}
